import {
    MoneyPoolCashin,
    MoneyPoolCashinTypePortion,
    MoneyPoolCashinTypeRepayment,
    MoneyPoolCashoutCustom,
    MoneyPoolCashoutReason,
    MoneyPoolCashoutTypeLoan,
    MoneyPoolTransactionTypes,
} from '../../../../../../utilities/helper/moneyPools'
import DeleteTransactionModal from 'components/modals/deleteTransaction'
import HashtagText from 'components/hashtagText'
import moment from 'jalali-moment'
import posthog from 'posthog-js'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ConvertJalaliDate } from 'utilities/helper/digitToMonth'
import close_icon from 'assets/images/rectangle/close.svg'
import useEditDescription from './useEditDescription'

interface ITransactionDetails {
    item: IMoneyPoolTransactionResult
    isManager: boolean
    setIsOpen: () => void
    /** tapping a #hashtag filters the transaction list by it */
    onHashtagClick?: (hashtag: string) => void
}

const DESCRIPTION_MAX_LENGTH = 300

function Row({ title, value }: { title: string; value: React.ReactNode }) {
    return (
        <div className="mt-1.5 flex items-baseline justify-between">
            <p className="text-text-secondary">{title}</p>
            <p className="font-bold text-[#565F6B]">{value}</p>
        </div>
    )
}

export default function TransactionDetail({
    item,
    isManager,
    setIsOpen,
    onHashtagClick,
}: ITransactionDetails) {
    const navigate = useNavigate()
    const { id } = useParams()
    const [showDeleteTrxModal, setShowDeleteTrxModal] = useState<boolean>(false)

    const isCashinType = item.type.substring(0, 2) === MoneyPoolCashin
    const canDelete = isManager && item.transaction === null

    // Editing a transaction is otherwise disabled — it used to allow changing
    // amounts, which broke the fund's books. The description is the carve-out:
    // it has no accounting meaning, and hashtags in it are only useful if a
    // typo can be corrected.
    const {
        isEditing,
        draft,
        setDraft,
        startEditing,
        cancelEditing,
        save,
        isSaving,
        description,
    } = useEditDescription({
        moneypoolId: Number(id),
        transactionId: item.id,
        isCashin: isCashinType,
        initialTag: item.tag,
    })

    const time =
        item.jalali_time &&
        moment(item.jalali_time, 'jYYYY-jMM-jDD HH:mm:ss.SSS').isValid()
            ? moment(item.jalali_time, 'jYYYY-jMM-jDD HH:mm:ss.SSS').format(
                  'HH:mm:ss'
              )
            : 'ساعت نامعتبر'

    return (
        <div className="pt-4 pb-6 text-right">
            {/* title row */}
            <div className="relative flex items-center justify-center px-4">
                <button
                    className="absolute left-4 top-1/2 -translate-y-1/2"
                    onClick={() => setIsOpen()}
                >
                    <img className="h-6 w-6" src={close_icon} alt="" />
                </button>
                <p className="text-base font-bold text-gray-1">
                    {MoneyPoolTransactionTypes[item.type]}
                </p>
            </div>

            <div className="mt-4 h-px bg-[#D4D4D4]" />

            {/* rows */}
            <div className="mt-2.5 px-4">
                <Row
                    title="مبلغ:"
                    value={
                        <>
                            {item.amount && item.amount.toLocaleString()} تومان
                        </>
                    }
                />

                {item.type === MoneyPoolCashinTypePortion ||
                item.type === MoneyPoolCashinTypeRepayment ? (
                    <>
                        <Row
                            title="مربوط به:"
                            value={
                                item.transaction
                                    ? item.transaction.receiver_name
                                    : item.poolship_name
                            }
                        />
                        <Row
                            title="توسط:"
                            value={
                                item.transaction
                                    ? item.transaction.payer_name
                                    : item.registrar_name
                            }
                        />
                    </>
                ) : (
                    <Row
                        title="دریافت کننده:"
                        value={
                            item.transaction
                                ? item.transaction.receiver_name
                                : item.poolship_name
                        }
                    />
                )}

                <Row
                    title={isCashinType ? 'تاریخ واریز:' : 'تاریخ برداشت:'}
                    value={
                        <>
                            {time}{' '}
                            {item.jalali_time &&
                                ConvertJalaliDate(item.jalali_time)}
                        </>
                    }
                />

                <Row
                    title="مانده صندوق پس از تراکنش:"
                    value={
                        <>
                            {item.balance_after_transaction &&
                                item.balance_after_transaction.toLocaleString()}{' '}
                            تومان
                        </>
                    }
                />
            </div>

            <div className="mx-4 mt-2.5 h-px bg-[#DCE0E5]" />

            {/* tag box */}
            <div
                className={`mx-4 mt-2.5 rounded-[10px] border border-dashed border-stroke px-3 pt-3 ${
                    isEditing ? 'pb-3' : 'pb-4'
                }`}
            >
                <div className="flex items-baseline justify-between gap-2">
                    <p className="text-text-secondary">توضیحات:</p>
                    {isManager && !isEditing && (
                        <button
                            type="button"
                            className="text-sm font-bold text-blue-1"
                            onClick={() => {
                                // The open half of the edit funnel;
                                // `transaction_description_edited` is the
                                // close. Without this, an abandoned edit is
                                // indistinguishable from one never started.
                                posthog.capture(
                                    'transaction_description_edit_started',
                                    {
                                        moneypool_id: Number(id),
                                        is_cashin: isCashinType,
                                        is_new: description === '',
                                    }
                                )
                                startEditing()
                            }}
                        >
                            {description === '' ? 'افزودن توضیح' : 'ویرایش'}
                        </button>
                    )}
                </div>

                {isEditing ? (
                    <>
                        <textarea
                            aria-label="توضیحات تراکنش"
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            rows={3}
                            placeholder="مثلاً: قبض برق #هزینه_جاری"
                            className="mt-2 w-full resize-none rounded-[8px] border border-stroke p-2 text-[#565F6B] outline-none focus:border-green-1 focus:outline-none"
                        />
                        <p className="mt-1 text-xs text-text-secondary">
                            هر واژه‌ای که با # شروع شود هشتگ می‌شود و با زدن روی
                            آن، تراکنش‌های همان هشتگ را می‌بینید.
                        </p>
                        <div className="mt-2 flex gap-2">
                            <button
                                type="button"
                                disabled={isSaving}
                                onClick={save}
                                className="h-[38px] flex-1 rounded-[10px] bg-green-1 text-sm font-bold text-white disabled:opacity-60"
                            >
                                {isSaving ? 'در حال ذخیره…' : 'ذخیره'}
                            </button>
                            <button
                                type="button"
                                disabled={isSaving}
                                onClick={cancelEditing}
                                className="h-[38px] flex-1 rounded-[10px] border border-stroke text-sm font-bold text-text-secondary disabled:opacity-60"
                            >
                                انصراف
                            </button>
                        </div>
                    </>
                ) : description === '' ? (
                    <p className="mt-4 mb-6 text-center text-[#565F6B] no-description">
                        توضیحی ثبت نشده است.
                    </p>
                ) : (
                    <HashtagText
                        text={description}
                        onHashtagClick={onHashtagClick}
                        className="mt-1.5 block whitespace-pre-wrap text-[#565F6B]"
                    />
                )}

                {item.type === MoneyPoolCashoutCustom && (
                    <p className="mt-1.5 text-[#565F6B]">
                        {MoneyPoolCashoutReason[item.reason]}
                    </p>
                )}
            </div>

            {/* buttons */}
            <div className="mt-8 flex gap-2 px-4">
                {canDelete && (
                    <button
                        className="h-[45px] flex-1 rounded-[10px] border border-danger font-bold text-danger"
                        onClick={() => setShowDeleteTrxModal(true)}
                    >
                        حذف تراکنش
                    </button>
                )}

                {item.type === MoneyPoolCashoutTypeLoan ? (
                    <button className="h-[45px] flex-1 rounded-[10px] bg-green-1 font-bold text-white">
                        اطلاعات وام
                    </button>
                ) : isManager ? (
                    <button
                        className="h-[45px] flex-1 rounded-[10px] bg-green-1 font-bold text-white"
                        onClick={() =>
                            navigate(
                                `/fund/financial-history/${id}/${item.poolship}`
                            )
                        }
                    >
                        اطلاعات عضو
                    </button>
                ) : (
                    <button
                        className="h-[45px] flex-1 rounded-[10px] bg-green-1 font-bold text-white"
                        onClick={() => setIsOpen()}
                    >
                        بازگشت
                    </button>
                )}
            </div>

            <DeleteTransactionModal
                show={showDeleteTrxModal}
                onHide={() => setShowDeleteTrxModal(false)}
                moneypoolId={Number(id)}
                transactionId={item.id}
                isMoneypoolCashin={isCashinType}
            />
        </div>
    )
}
